import { useUser } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FormData } from "../types";
import { createAndUploadPDF } from "../utils/pdfUtils";

export const useInvoiceSubmit = (onSuccess?: () => void) => {
  const user = useUser();

  const onSubmit = async (values: FormData, mode: "create" | "edit" | "schedule" = "create", existingInvoiceId?: string) => {

    if (!user) {
      console.error('useInvoiceSubmit - No user found');
      toast.error("You must be logged in to create an invoice");
      return { invoiceId: "" };
    }

    try {
      const subtotal = values.lineItems.reduce((total, item) => {
        return total + (item.quantity * item.unitPrice);
      }, 0);

      const totalTaxAmount = values.taxes?.reduce((total, tax) => {
        return total + (subtotal * tax.percentage / 100);
      }, 0) || 0;

      // Ensure recipients_emails is a properly formatted array
      const recipientsEmails = values.recipients_emails || [];

      const invoiceData = {
        user_id: user.id,
        invoice_number: values.invoiceNumber,
        issue_date: values.issueDate.toISOString(),
        due_date: values.dueDate.toISOString(),
        business_name: values.businessName,
        business_tax_id: values.businessTaxId,
        business_iban: values.businessIban,
        business_swift: values.businessSwift,
        business_email: values.businessEmail,
        business_phone: values.businessPhone,
        recipient_name: values.recipientName,
        recipient_email: values.recipientEmail,
        recipient_address: values.recipientAddress,
        client_tax_id: values.clientTaxId,
        client_phone: values.clientPhone,
        description: values.lineItems[0].description,
        quantity: values.lineItems[0].quantity,
        unit_price: values.lineItems[0].unitPrice,
        subtotal: subtotal,
        total_tax_amount: totalTaxAmount,
        amount: subtotal + totalTaxAmount,
        bank_account_holder: values.bankAccountHolder,
        bank_iban: values.bankIban,
        bank_name: values.bankName,
        bank_swift: values.bankSwift,
        payment_reference: values.paymentReference,
        taxes: values.taxes,
        recipients_emails: recipientsEmails,
        currency: values.currency,
      };

      let result;

      // Treat "schedule" mode the same as "edit" mode for database operations
      if ((mode === "edit" || mode === "schedule") && existingInvoiceId) {

        // Only update the PDF if needed in edit/schedule mode
        const { fileName, publicUrl } = await createAndUploadPDF(values, user);

        // Update with the new file details
        const updatedData = {
          ...invoiceData,
          file_name: fileName,
          file_path: publicUrl,
        };

        const { data, error } = await supabase
          .from("invoices")
          .update(updatedData)
          .eq('id', existingInvoiceId)
          .select()
          .single();

        if (error) {
          console.error('useInvoiceSubmit - Update error:', error);
          throw error;
        }

        result = data;
      } else {

        // Always create a new PDF for new invoices
        const { fileName, publicUrl } = await createAndUploadPDF(values, user);

        // Insert new invoice with file details
        const newData = {
          ...invoiceData,
          file_name: fileName,
          file_path: publicUrl,
        };

        const { data, error } = await supabase
          .from("invoices")
          .insert(newData)
          .select()
          .single();

        if (error) {
          console.error('useInvoiceSubmit - Insert error:', error);
          throw error;
        }

        result = data;
      }

      onSuccess?.();
      return { invoiceId: result.id };
    } catch (error: any) {
      console.error('useInvoiceSubmit - Error:', error);
      toast.error(error.message || "Failed to process invoice");
      throw error;
    }
  };

  return { onSubmit, user };
};
